import {
  BrandIReckonInRoomCredentials,
  FrontDeskIReckonInRoomCredentials,
  LocationIReckonInRoomCredentials,
  RoomIReckonInRoomCredentials,
} from '@era/locations-sdk';
import { createEntityAdapter, EntityAdapter, EntityState } from '@ngrx/entity';
import { CategoryType } from '../shared/types';

export interface InRoomCredentialsConfiguration {
  type: CategoryType;
  id: string;
  iReckonInRoomCredentialsId: string;
}

type AnyInRoomCredentialsConfiguration =
  | BrandIReckonInRoomCredentials
  | LocationIReckonInRoomCredentials
  | RoomIReckonInRoomCredentials
  | FrontDeskIReckonInRoomCredentials;

/**
 * Generic function to convert any in-room credentials configuration to the unified format.
 * Automatically determines the category type and ID based on the input configuration.
 */
export function inRoomCredentialsConfigurationFrom(
  configuration: AnyInRoomCredentialsConfiguration
): InRoomCredentialsConfiguration {
  // Type guard functions to determine the category type
  if ('brandUniqueId' in configuration) {
    return {
      type: 'Brand',
      id: configuration.brandUniqueId,
      iReckonInRoomCredentialsId: configuration.iReckonInRoomCredentialsId,
    };
  }
  if ('locationUniqueId' in configuration) {
    return {
      type: 'Location',
      id: configuration.locationUniqueId,
      iReckonInRoomCredentialsId: configuration.iReckonInRoomCredentialsId,
    };
  }
  if ('roomUniqueId' in configuration) {
    return {
      type: 'Room',
      id: configuration.roomUniqueId,
      iReckonInRoomCredentialsId: configuration.iReckonInRoomCredentialsId,
    };
  }
  if ('frontDeskUniqueId' in configuration) {
    return {
      type: 'FrontDesk',
      id: configuration.frontDeskUniqueId,
      iReckonInRoomCredentialsId: configuration.iReckonInRoomCredentialsId,
    };
  }
  throw new Error('Unknown in-room credentials configuration type');
}

export type CategoryInRoomCredentialsState = EntityState<InRoomCredentialsConfiguration>;

export function selectId(configuration: InRoomCredentialsConfiguration): string {
  return `${configuration.type}-${configuration.id}`;
}

export const adapter: EntityAdapter<InRoomCredentialsConfiguration> =
  createEntityAdapter<InRoomCredentialsConfiguration>({
    selectId,
  });

export const initialState: CategoryInRoomCredentialsState = adapter.getInitialState();

/**
 * Partial state interface for components that use category in-room credentials store
 * Includes both categoryInRoomCredentials and inRoomCredentials feature states
 */
export interface CategoryInRoomCredentialsPartialState {
  categoryInRoomCredentials: CategoryInRoomCredentialsState;
  inRoomCredentials: import('../in-room-credentials/in-room-credentials.state').InRoomCredentialsState;
}
